import { useState } from "react";
import { api, type TenantRole } from "../api";
import { useAsync } from "../hooks";

interface Org {
  tenantId: string;
  tenantName: string;
  tenantRole: TenantRole;
  active: boolean;
}

const COLLAPSED = 3;
const canManage = (role: TenantRole): boolean => role === "owner" || role === "admin";

/**
 * Multi-org: lists the orgs the user belongs to (active first, so it stays visible when collapsed),
 * switches between them, renames the ones they own/admin, and creates new ones. Rendered in the rail
 * for any session user with ≥1 org. Only the first {@link COLLAPSED} show until "View more" expands
 * the rest. Create/rename use an INLINE input in the rail (no browser prompt); switch / create / rename
 * re-issue the session server-side and reload so every view refetches under the current org. Rename
 * authz is re-checked server-side.
 */
export function OrgSwitcher() {
  const { data } = useAsync<Org[]>(() => api("/session/orgs"), []);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // `editing`: null (idle) | "new" (creating) | a tenantId (renaming that org).
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  if (!data || data.length === 0) return null;

  // Active first (always in the collapsed top-3), then alphabetical.
  const orgs = [...data].sort((a, b) =>
    a.active === b.active ? a.tenantName.localeCompare(b.tenantName) : a.active ? -1 : 1,
  );
  const active = orgs.find((o) => o.active);
  const shown = showAll ? orgs : orgs.slice(0, COLLAPSED);
  const hiddenCount = orgs.length - shown.length;

  const cancel = () => {
    setEditing(null);
    setDraft("");
  };
  const startNew = () => {
    setEditing("new");
    setDraft("");
  };
  const startRename = (o: Org) => {
    setEditing(o.tenantId);
    setDraft(o.tenantName);
  };

  async function switchTo(tenantId: string) {
    if (busy || tenantId === active?.tenantId) return;
    setBusy(true);
    try {
      await api("/session/switch", { method: "POST", body: JSON.stringify({ tenantId }) });
      location.reload();
    } catch {
      setBusy(false);
    }
  }

  async function submit() {
    const name = draft.trim();
    if (!name || busy) return;
    // Rename to the same name is a no-op — just close the editor.
    const target = editing !== "new" ? orgs.find((o) => o.tenantId === editing) : undefined;
    if (target && name === target.tenantName) return cancel();
    setBusy(true);
    try {
      if (editing === "new") {
        await api("/session/workspaces", { method: "POST", body: JSON.stringify({ name }) });
      } else {
        await api(`/session/orgs/${editing}`, { method: "PATCH", body: JSON.stringify({ name }) });
      }
      location.reload();
    } catch {
      setBusy(false);
    }
  }

  const editForm = (placeholder: string, submitLabel: string) => (
    <form
      className="org-edit"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <input
        autoFocus
        className="org-edit-input"
        value={draft}
        disabled={busy}
        maxLength={120}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") cancel();
        }}
      />
      <div className="org-edit-actions">
        <button type="submit" className="quiet-btn" disabled={busy || !draft.trim()}>
          {submitLabel}
        </button>
        <button type="button" className="quiet-btn" disabled={busy} onClick={cancel}>
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div className="org-switcher">
      <label className="nav-section">Organization</label>
      <ul className="org-list">
        {shown.map((o) => (
          <li key={o.tenantId} className={`org-item${o.active ? " is-active" : ""}`}>
            {editing === o.tenantId ? (
              editForm("Workspace name", "Save")
            ) : (
              <>
                <button
                  className="org-pick"
                  disabled={busy}
                  aria-current={o.active || undefined}
                  title={o.tenantName}
                  onClick={() => void switchTo(o.tenantId)}
                >
                  {o.tenantName}
                </button>
                {canManage(o.tenantRole) && (
                  <button
                    className="quiet-btn org-rename"
                    disabled={busy}
                    title="Rename workspace"
                    aria-label={`Rename ${o.tenantName}`}
                    onClick={() => startRename(o)}
                  >
                    Rename
                  </button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {hiddenCount > 0 ? (
        <button className="quiet-btn org-more" disabled={busy} onClick={() => setShowAll(true)}>
          View {hiddenCount} more
        </button>
      ) : showAll && orgs.length > COLLAPSED ? (
        <button className="quiet-btn org-more" disabled={busy} onClick={() => setShowAll(false)}>
          Show less
        </button>
      ) : null}
      {editing === "new" ? (
        editForm("New workspace name", "Create")
      ) : (
        <button className="quiet-btn org-new" disabled={busy} onClick={startNew}>
          + New workspace
        </button>
      )}
    </div>
  );
}
